import { Component, OnDestroy, OnInit, TemplateRef } from '@angular/core';
import { ActivatedRoute } from '@angular/router';
import { finalize, Subscription } from 'rxjs';
import moment from 'moment';
import { PartyVisit } from '../../../attendance/attendance.model';
import { PaginatedResponse } from '../../../common/models/common.model';
import { NgbModal, NgbModalRef } from '@ng-bootstrap/ng-bootstrap';
import { PartyVisitsService } from '../../party-visits.service';
import { EmployeesService } from '../../../employees/employees.service';
import { Employee } from '../../../employees/employees.model';
import { VisitStatus } from '../../party-visits.model';
import { DealersService } from '../../../dealers/dealers.service';
import { Dealer } from '../../../dealers/dealers.model';
import { ErrorParserService } from '../../../services/error-parser.service';
import { buildFilterQuery } from '../../../utils';

type DateFilterMode = 'all' | 'date' | 'range' | 'month_year' | 'year';

@Component({
  selector: 'app-employee-visit-details',
  standalone: false,
  templateUrl: './employee-visit-details.component.html',
})
export class EmployeeVisitDetailsComponent implements OnInit, OnDestroy {
  protected isDataLoading: boolean = true;
  protected isPartyVisitsLoading: boolean = false;
  protected partyVisits: PartyVisit[] = [];
  protected groupedVisits: { date: string; visits: PartyVisit[] }[] = [];
  protected currentPage: number = 1;
  protected totalCount: number = 0;
  protected hasNextPage = false;
  protected hasPreviousPage = false;

  protected employeeId!: string;
  protected employeeObj?: Employee;
  protected selectedVisit?: PartyVisit;
  protected modalRef?: NgbModalRef;
  protected userId!: string;

  protected selectedStatus: string | null = null;
  protected selectedDealer: string | null = null;
  protected partyVisitsYears: number[] = [];
  protected visitStatuses: string[] = Object.values(VisitStatus);
  protected dealersList: Dealer[] = [];

  protected readonly monthOptions = moment.months().map((label, index) => ({ label, value: index + 1 }));

  protected readonly dateFilterModes: { label: string; value: DateFilterMode }[] = [
    { label: 'All dates', value: 'all' },
    { label: 'Specific Date', value: 'date' },
    { label: 'Date Range', value: 'range' },
    { label: 'Month + Year', value: 'month_year' },
    { label: 'Year', value: 'year' },
  ];

  protected dateFilterMode: DateFilterMode = 'year';
  protected dateFilters: {
    date: string;
    start_date: string;
    end_date: string;
    month: number | null;
    year: number | null;
  } = {
    date: '',
    start_date: '',
    end_date: '',
    month: null,
    year: moment().year(),
  };

  protected isExportingVisits: boolean = false;

  /** Client-side validation errors, keyed by request param name. */
  protected clientErrors: Record<string, string> = {};
  /** Field errors returned by the API (400), keyed by request param name. */
  protected fieldErrors: Record<string, string> = {};
  /** Non-field API errors, rendered above the filters. */
  protected generalError: any = null;

  private _subscription: Subscription = new Subscription();

  constructor(
    private route: ActivatedRoute,
    private partyVisitsService: PartyVisitsService,
    private employeeService: EmployeesService,
    private dealersService: DealersService,
    private errorParser: ErrorParserService,
    private modalService: NgbModal
  ) {}

  ngOnInit(): void {
    this.employeeId = this.route.snapshot.params['id'];
    this._getEmployeeDetails();
    this._loadDealers();
  }

  protected get isApplyDisabled(): boolean {
    return this.isPartyVisitsLoading || Object.keys(this.clientErrors).length > 0;
  }

  protected errorFor(field: string): string | null {
    return this.clientErrors[field] || this.fieldErrors[field] || null;
  }

  protected onDateFilterModeChange(): void {
    this.fieldErrors = {};
    this._validateFilters();
  }

  /** Re-validates as the user edits; the fetch only happens on Apply. */
  protected onFilterInputChange(): void {
    this._validateFilters();
  }

  protected onApplyFilters(): void {
    this._validateFilters();
    if (Object.keys(this.clientErrors).length) return;

    this.currentPage = 1;
    this._getPartyVisits();
  }

  protected onClearFilters(): void {
    this.selectedStatus = null;
    this.selectedDealer = null;
    this.dateFilterMode = 'all';
    this.dateFilters = { date: '', start_date: '', end_date: '', month: null, year: moment().year() };
    this.clientErrors = {};
    this.fieldErrors = {};
    this.generalError = null;

    this.currentPage = 1;
    this._getPartyVisits();
  }

  protected onPageChange(page: number): void {
    this.currentPage = page;
    this._getPartyVisits();
  }

  /** Exports this employee's visits under the filters currently applied on the page. */
  protected onExportVisits(): void {
    this.isExportingVisits = true;

    const sub = this.dealersService
      .exportVisits({
        employee: this.employeeId,
        dealer: this.selectedDealer || undefined,
        ...this._buildExportDateParams(),
      })
      .pipe(finalize(() => (this.isExportingVisits = false)))
      .subscribe({
        next: (blob: Blob) => {
          const url = URL.createObjectURL(blob);
          const a = document.createElement('a');
          a.href = url;
          a.download = this._exportFilename();
          a.click();
          URL.revokeObjectURL(url);
        },
      });
    this._subscription.add(sub);
  }

  protected getStatusBadgeClass(status: string): string {
    switch (status) {
      case VisitStatus.COMPLETED:
        return 'bg-success';
      case VisitStatus.CANCELLED:
        return 'bg-danger';
      case VisitStatus.PENDING:
        return 'bg-warning';
      case VisitStatus.CHECKED_IN:
        return 'bg-primary';
      default:
        return 'bg-secondary';
    }
  }

  protected viewVisitDetails(visit: PartyVisit, content: TemplateRef<any>): void {
    this.selectedVisit = visit;
    this.modalRef = this.modalService.open(content, {
      size: 'lg',
      backdrop: 'static',
      scrollable: true,
    });
  }

  private _getEmployeeDetails(): void {
    const sub = this.employeeService.get(this.employeeId).subscribe({
      next: (res: Employee) => {
        this.employeeObj = res;
        this.userId = this.employeeObj.user.id;
        this._getInitialData();
      },
      error: () => (this.isDataLoading = false),
    });
    this._subscription.add(sub);
  }

  private _getInitialData(): void {
    this.isDataLoading = true;
    const sub = this.partyVisitsService
      .years()
      .pipe(finalize(() => (this.isDataLoading = false)))
      .subscribe({
        next: ({ years }) => {
          this.partyVisitsYears = years?.length ? years : [moment().year()];
          this.dateFilters.year = this.partyVisitsYears[0];
          this._getPartyVisits();
        },
      });
    this._subscription.add(sub);
  }

  private _loadDealers(): void {
    const sub = this.dealersService.lighter_list().subscribe({
      next: (dealers: Dealer[]) => (this.dealersList = dealers),
    });
    this._subscription.add(sub);
  }

  private _getPartyVisits(): void {
    this.isPartyVisitsLoading = true;
    this.fieldErrors = {};
    this.generalError = null;

    const sub = this.partyVisitsService
      .party_visits(this._buildQuery())
      .pipe(finalize(() => (this.isPartyVisitsLoading = false)))
      .subscribe({
        next: (res: PaginatedResponse<PartyVisit>) => {
          this.partyVisits = res.results || [];
          this.totalCount = res.count;
          this.hasNextPage = !!res.next;
          this.hasPreviousPage = !!res.previous;
          this._groupVisitsByDate();
        },
        error: (err: any) => {
          this.partyVisits = [];
          this.groupedVisits = [];
          this.totalCount = 0;
          this.hasNextPage = false;
          this.hasPreviousPage = false;
          this._applyApiErrors(err);
        },
      });
    this._subscription.add(sub);
  }

  /** Only the params for the active date mode are sent; empty values are dropped. */
  private _buildQuery(): Record<string, string> {
    const { date, start_date, end_date, month, year } = this.dateFilters;

    const dateParams: Record<string, any> = {};
    switch (this.dateFilterMode) {
      case 'date':
        dateParams['date'] = date;
        break;
      case 'range':
        dateParams['start_date'] = start_date;
        dateParams['end_date'] = end_date;
        break;
      case 'month_year':
        dateParams['month'] = month;
        dateParams['year'] = year;
        break;
      case 'year':
        dateParams['year'] = year;
        break;
    }

    return buildFilterQuery({
      page: this.currentPage,
      employee_id: this.userId,
      visit_status: this.selectedStatus,
      dealer_id: this.selectedDealer,
      ...dateParams,
    });
  }

  /** Only the date params for the active mode are exported, matching the visible list. */
  private _buildExportDateParams(): {
    date?: string;
    start_date?: string;
    end_date?: string;
    month?: number | null;
    year?: number | null;
  } {
    const { date, start_date, end_date, month, year } = this.dateFilters;

    switch (this.dateFilterMode) {
      case 'date':
        return { date };
      case 'range':
        return { start_date, end_date };
      case 'month_year':
        return { month, year };
      case 'year':
        return { year };
      default:
        return {};
    }
  }

  private _exportFilename(): string {
    const employee = `${this.employeeObj?.user?.first_name ?? ''} ${this.employeeObj?.user?.last_name ?? ''}`
      .trim()
      .toLowerCase()
      .replace(/[^a-z0-9]+/g, '_');
    const { date, start_date, end_date, month, year } = this.dateFilters;

    let scope: string;
    switch (this.dateFilterMode) {
      case 'date':
        scope = date;
        break;
      case 'range':
        scope = `${start_date}_to_${end_date}`;
        break;
      case 'month_year':
        scope = `${year}_${String(month).padStart(2, '0')}`;
        break;
      case 'year':
        scope = `${year}`;
        break;
      default:
        scope = 'all_dates';
    }

    return `visits_${employee || 'employee'}_${scope}.xlsx`;
  }

  private _validateFilters(): void {
    const errors: Record<string, string> = {};
    const { date, start_date, end_date, month, year } = this.dateFilters;

    switch (this.dateFilterMode) {
      case 'date':
        if (!date) errors['date'] = 'Select a date.';
        break;
      case 'range':
        if (!start_date) errors['start_date'] = 'Select a start date.';
        if (!end_date) errors['end_date'] = 'Select an end date.';
        if (start_date && end_date && moment(start_date).isAfter(moment(end_date))) {
          errors['end_date'] = 'End date must be on or after the start date.';
        }
        break;
      case 'month_year':
        if (!month) errors['month'] = 'Select a month.';
        // Mirrors the API rule: month is only accepted alongside a year.
        if (!year) errors['year'] = "'year' is required when filtering by 'month'.";
        break;
      case 'year':
        if (!year) errors['year'] = 'Select a year.';
        break;
    }

    this.clientErrors = errors;
  }

  /** Splits a 400 into per-field messages (shown inline) and everything else. */
  private _applyApiErrors(err: any): void {
    const parsed = this.errorParser.parseError(err);
    const filterFields = ['date', 'start_date', 'end_date', 'month', 'year', 'visit_status', 'dealer_id'];

    const fieldErrors: Record<string, string> = {};
    parsed.forEach(({ field, message }) => {
      if (field && filterFields.includes(field)) fieldErrors[field] = message;
    });

    this.fieldErrors = fieldErrors;
    this.generalError = Object.keys(fieldErrors).length === parsed.length ? null : err;
  }

  private _groupVisitsByDate(): void {
    const grouped: Record<string, PartyVisit[]> = {};
    this.partyVisits.forEach(visit => {
      const date = moment(visit.created_at).format('YYYY-MM-DD');
      if (!grouped[date]) {
        grouped[date] = [];
      }
      grouped[date].push(visit);
    });

    this.groupedVisits = Object.keys(grouped)
      .sort((a, b) => moment(b).diff(moment(a)))
      .map(date => ({
        date,
        visits: grouped[date],
      }));
  }

  ngOnDestroy(): void {
    this._subscription.unsubscribe();
    this.modalRef?.dismiss();
  }
}
